import { useMemo } from 'react';

import { DEFAULT_SLOT_KEY, SlotsSelections } from 'features/menu/components/product-wizard/types';
import { computeMenuItemPrice } from 'features/menu/components/product-wizard/utils';

import { EntryTypeEnum, IPriceDto } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getComboSlotItemPrice, getFirstDefaultId, isItem } from 'state/unified-menu/utils';

export const useProductTotalPrice = ({
  id,
  selectedProduct,
  slotsSelections,
  productQuantity,
  cartIdEditing,
  rewardId,
}: {
  id: string;
  selectedProduct: ILocalizedMenuEntry;
  slotsSelections: SlotsSelections;
  productQuantity: number;
  cartIdEditing?: string;
  rewardId?: string;
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu, indexedRewards } = useUnifiedMenuContext();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const availablePoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);

  const discountPrice = useMemo(() => {
    const individualItemPrice = selectedProduct?.price?.[posServiceMode] ?? 0;
    if (appliedLoyaltyRewards[cartIdEditing ?? '']) {
      return individualItemPrice;
    }

    const productQuantityForDiscount = enableRewardUnstacking
      ? Math.min(1, productQuantity)
      : productQuantity;

    const reward = indexedRewards.find(rewardId);
    const loyaltyReward = availableLoyaltyRewardsMap[id];
    if (reward && loyaltyReward) {
      const maxApplicationsByPoints = Math.floor(availablePoints / loyaltyReward.pointCost);
      const timesApplied = Math.min(maxApplicationsByPoints, productQuantityForDiscount);
      return individualItemPrice * timesApplied;
    }

    return 0;
  }, [
    selectedProduct?.price,
    posServiceMode,
    appliedLoyaltyRewards,
    cartIdEditing,
    enableRewardUnstacking,
    productQuantity,
    indexedRewards,
    rewardId,
    availableLoyaltyRewardsMap,
    id,
    availablePoints,
  ]);

  const totalPrice = useMemo(() => {
    return (
      computeTotalPrice(indexedMenu, posServiceMode, selectedProduct, slotsSelections) *
        productQuantity -
      discountPrice
    );
  }, [
    indexedMenu,
    posServiceMode,
    selectedProduct,
    slotsSelections,
    productQuantity,
    discountPrice,
  ]);

  const formattedTotalPrice = useMemo(() => formatCurrencyForLocale(totalPrice), [
    formatCurrencyForLocale,
    totalPrice,
  ]);

  return {
    totalPrice,
    formattedTotalPrice,
  };
};

export function computeTotalPrice(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections
): number {
  if (isItem(selectedProduct)) {
    const modifierSelections = slotsSelections[DEFAULT_SLOT_KEY]?.modifiers ?? {};
    return computeMenuItemPrice(
      indexedMenu,
      selectedProduct,
      posServiceMode,
      undefined,
      modifierSelections
    );
  }

  let comboPrice = selectedProduct?.price?.[posServiceMode] ?? 0;
  for (const comboSlotRef of selectedProduct?.options?.entries ?? []) {
    const comboSlot = indexedMenu.get(comboSlotRef.entryId);
    const defaultComboSlotPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);

    let item: ILocalizedMenuEntry;
    let contextualPrice: IPriceDto;
    const slotSelection = slotsSelections[comboSlot.id];
    if (slotSelection?.itemId) {
      item = indexedMenu.get(slotSelection.itemId, { type: EntryTypeEnum.ITEM });
      contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, slotSelection.itemId);
    } else {
      item = indexedMenu.get(getFirstDefaultId(comboSlot));
      contextualPrice = defaultComboSlotPrice;
    }

    const modifierSelections = slotSelection?.modifiers ?? {};
    const itemPrice = computeMenuItemPrice(
      indexedMenu,
      item,
      posServiceMode,
      contextualPrice,
      modifierSelections
    );

    // Only add price for upsell combo slots or customizations
    const defaultPrice = defaultComboSlotPrice?.[posServiceMode] ?? 0;
    if (itemPrice !== defaultPrice) {
      comboPrice += itemPrice;
    }
  }
  return comboPrice;
}
